import React, { useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';
import {
  ContaBanco,
  InputNumber,
  InputPercent,
  InputText,
  Loja,
} from '~/components/NovosInputs';
import Separator from '~/components/Separator';
import { bandeirasDefault } from '~/pages/CadastroDeAdministradora/DefaultData';
import { useAdministradora } from '../../../hooks/useAdministradora';
import { ContainerBandeira, ContainerButtons } from './styles';
import { insertOrUpdate } from '~/utils/masterDetail';
import { nanoid } from 'nanoid';
import { transformAsCurrency } from '~/utils/functions';
import { rowsProps } from '~/pages/CadastroDeAdministradora/types';
import { toast } from 'react-toastify';

export const FormBandeiras: React.FC = () => {
  const {
    formBandeiras: {
      register,
      control,
      formState: { errors },
      getValues,
      clearErrors,
      setValue,
      handleSubmit,
      reset,
      watch,
    },
    setRows,
    masterDetail,
    setMasterDetail,
    bandeiraId,
    setBandeiraId,
    setLoadingTable,
    loadingTable,
    setIsEditing,
    isEditing,
    rows,
    loja,
  } = useAdministradora();

  useEffect(() => {
    setValue('cod_loja', {
      value: loja.cod_loja,
      label: `${loja.des_loja} (${loja.des_cidade})`,
    });
  }, [loja]);

  const onSubmitBandeira = handleSubmit(async (data) => {
    setLoadingTable(true);
    try {
      const cod_seq_bandeira = rows.find(
        (item) => item.uuid === bandeiraId,
      )?.cod_seq_bandeira;

      const formattedValues = {
        ...data,
        uuid: isEditing ? bandeiraId : nanoid(),
        cod_seq_bandeira: isEditing ? cod_seq_bandeira : undefined,
        cod_loja: data.cod_loja,
        cod_cc: data.cod_cc,
        des_bandeira: data.des_bandeira.trim(),
        per_taxa_credito: transformAsCurrency(data.per_taxa_credito),
        num_condicao_credito: data.num_condicao_credito,
        per_taxa_debito: transformAsCurrency(data.per_taxa_debito),
        num_condicao_debito: data.num_condicao_debito,
        per_taxa_parcelado: transformAsCurrency(data.per_taxa_parcelado),
        num_condicao_parcela: data.num_condicao_parcela,
        flg_remove: false,
      };

      const existingDesc = rows.find(
        (item: rowsProps) =>
          item.des_bandeira.trim() === formattedValues.des_bandeira &&
          item.uuid !== formattedValues.uuid,
      );
      if (existingDesc) {
        setLoadingTable(false);
        return toast.warn('Já existe uma bandeira com essa descrição');
      }

      const rowsDetail: any = await insertOrUpdate(
        'bandeiras',
        formattedValues,
        masterDetail,
        setMasterDetail,
      );

      setRows(rowsDetail);
      setBandeiraId('');
      setIsEditing(false);

      reset(bandeirasDefault);
      setLoadingTable(false);
    } catch (error) {
      setLoadingTable(false);
    }
  });

  const onCancelBandeira = () => {
    reset(bandeirasDefault);
    setBandeiraId('');
    setIsEditing(false);
  };

  return (
    <ContainerBandeira>
      <Col sm={12} className="mt-4">
        <Separator labelText="Bandeiras" />
      </Col>
      <Row>
        <Col sm={12} md={4}>
          <InputText
            label="Bandeira *"
            name="des_bandeira"
            placeholder="Digite o Nome da Bandeira"
            caseInput="upper"
            maxLength={50}
            control={control}
            register={register}
            isError={!!errors.des_bandeira}
          />
        </Col>
        <Col sm={12} md={4}>
          <Loja
            label="Loja *"
            name="cod_loja"
            placeholder="Selecione..."
            register={register}
            control={control}
            retriever={loja.cod_loja}
            isError={false}
            getValues={getValues}
            disabled={false}
            changeSelected={(ev) => {
              setValue('cod_loja', ev);
            }}
          />
        </Col>
        <Col sm={12} md={4}>
          <ContaBanco
            label="Conta *"
            placeholder="Selecione..."
            control={control}
            isError={!!errors.cod_cc}
            loja={watch('cod_loja')?.value}
            register={register}
            name="cod_cc"
            changeSelected={(selected) => {
              clearErrors('cod_cc');
              setValue('cod_cc', selected);
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={2}>
          <InputPercent
            label="% Créd. *"
            placeholder="0,00"
            name="per_taxa_credito"
            register={register}
            control={control}
            max={100}
            maxLength={6}
            min={0}
            isError={!!errors.per_taxa_credito}
          />
        </Col>
        <Col sm={12} md={1}>
          <InputNumber
            label="Cond. Créd. *"
            placeholder="0"
            name="num_condicao_credito"
            register={register}
            control={control}
            max={999}
            maxLength={3}
            min={0}
            isError={!!errors.num_condicao_credito}
          />
        </Col>
        <Col sm={12} md={2}>
          <InputPercent
            label="% Déb. *"
            placeholder="0,00"
            name="per_taxa_debito"
            register={register}
            control={control}
            max={100}
            maxLength={6}
            min={0}
            isError={!!errors.per_taxa_debito}
          />
        </Col>
        <Col sm={12} md={1}>
          <InputNumber
            label="Cond. Déb. *"
            placeholder="0"
            name="num_condicao_debito"
            register={register}
            control={control}
            max={999}
            maxLength={3}
            min={0}
            isError={!!errors.num_condicao_debito}
          />
        </Col>
        <Col sm={12} md={2}>
          <InputPercent
            label="% Parcelado *"
            placeholder="0,00"
            name="per_taxa_parcelado"
            register={register}
            control={control}
            max={100}
            maxLength={6}
            min={0}
            isError={!!errors.per_taxa_parcelado}
          />
        </Col>
        <Col sm={12} md={1}>
          <InputNumber
            label="Cond. Parc. *"
            placeholder="0"
            name="num_condicao_parcela"
            register={register}
            control={control}
            max={999}
            maxLength={3}
            min={0}
            isError={!!errors.num_condicao_parcela}
          />
        </Col>
        <Col
          sm={12}
          md={3}
          className="d-flex align-items-end justify-content-between"
        >
          <ContainerButtons>
            <CustomButtonNovo
              disabled={false}
              onClick={onSubmitBandeira}
              label="Confirmar"
              width="165px"
              className="buttonConfirm"
              showLoadingIcon={loadingTable}
            />
            <CustomButtonNovo
              variant="cancel"
              disabled={false}
              onClick={onCancelBandeira}
              label="Cancelar"
              width="165px"
              className="buttonCancel"
            />
          </ContainerButtons>
        </Col>
      </Row>
    </ContainerBandeira>
  );
};
